import { Component, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    selectShipmentEditorAllCommodities,
    selectShipmentEditorPackages,
    selectShipmentEditorShipment
} from '../../../store/shipment-editor/shipment-editor.state';
import { map, startWith } from 'rxjs/operators';
import { CountriesService } from '../../shared/services/countries.service';
import { matchChoices } from '../../../utils/match-choices';
import { CurrencyService } from '../../shared/services/currency.service';
import { unitsOfMeasure } from '../../../list/units-of-measure';
import { Commodity } from '../../../models/commodity';
import {
    shipmentEditorAddCommodity,
    shipmentEditorAddEmptyCommodity, shipmentEditorDeleteCommodities, shipmentEditorMoveCommodities,
    shipmentEditorPatchCommodities
} from '../../../store/shipment-editor/shipment-editor.actions';
import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef, MatTable
} from '@angular/material/table';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { Country } from '../../../list/countries';
import { Choice } from '../../../models/choice';
import { BehaviorSubject } from 'rxjs';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ShipmentCommodity } from '../../../models/shipment-commodity';
import { DigitsPipe } from '../../shared/pipes/digits.pipe';

type CommodityRow = {
    kind: 'header' | 'commodity';
    commodity?: ShipmentCommodity;
    commodityIndex?: number;
    packageIndex?: number;
    caption?: string;
};

@Component({
    selector: 'app-shipment-commodities-table',
    imports: [
        CurrencyPipe,
        DecimalPipe,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatButton,
        MatCell,
        MatCellDef,
        MatCheckbox,
        MatColumnDef,
        MatHeaderCell,
        MatHeaderRow,
        MatHeaderRowDef,
        MatInput,
        MatOption,
        MatRow,
        MatRowDef,
        MatTable,
        MatHeaderCellDef,
        AsyncPipe,
        MatMenuItem,
        MatMenu,
        MatMenuTrigger,
        DigitsPipe
    ],
    templateUrl: './shipment-commodities-table.component.html',
    styleUrl: './shipment-commodities-table.component.scss'
})
export class ShipmentCommoditiesTableComponent {

    selectedIds = new Set<number>();
    public unitsOfMeasure = unitsOfMeasure;

    displayedColumns = [
        'select', 'quantity', 'description', 'country', 'weight', 'unitOfMeasure', 'price', 'pieces', 'length', 'width', 'height'
    ];

    constructor(private store: Store<AppState>, private countriesService: CountriesService, private currencyService: CurrencyService) {
    }

    public packages = toSignal(this.store.select(selectShipmentEditorPackages), { initialValue: [] });
    public commodities = toSignal(this.store.select(selectShipmentEditorAllCommodities));
    public currency = toSignal(this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => shipment.currency)
    ), { initialValue: 'CAD' });
    public weightUnit = toSignal(this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => shipment.weightUnit),
        map(unit => unit === 'KG' ? 'KGs' : 'LBs')
    ), { initialValue: 'KGs' });
    public dimensionUnit = toSignal(this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => shipment.dimensionUnit)
    ), { initialValue: 'CM' });
    public countrySearch$ = new BehaviorSubject<string>('');
    readonly countryFilter$ = this.countrySearch$.pipe(
        startWith(''),
        map(value => this.countryFilter(value)),
    );
    public totalWeight = computed(() => this.commodities().reduce((acc, cur) => acc + cur.weight * cur.quantity, 0));
    public totalValue = computed(() => this.commodities().reduce((acc, cur) => acc + cur.price * cur.quantity, 0));

    public commodityRows = computed<CommodityRow[]>(() => {
        const items = this.commodities() ?? [];

        if (items.length === 0) {
            return [];
        }

        // Only show headers if there is more than one package
        const showHeaders = this.packages().length > 1;

        const rows: CommodityRow[] = [];
        let lastPackageIndex: number | null = null;
        let lastCommodityIndex = 0;

        for (const c of items) {
            if (showHeaders && c.packageIndex !== lastPackageIndex) {
                rows.push({
                    kind: 'header',
                    packageIndex: c.packageIndex,
                    caption: this.packages()[c.packageIndex].description
                });
                lastPackageIndex = c.packageIndex;
            }

            rows.push({
                kind: 'commodity',
                commodity: c,
                commodityIndex: lastCommodityIndex++
            });
        }

        return rows;
    });

    isHeaderRow = (_: number, row: CommodityRow) => row.kind === 'header';
    isCommodityRow = (_: number, row: CommodityRow) => row.kind === 'commodity';

    countryFilter(value: string): Choice[] {
        return this.countriesService.countryFilter(value).map(c => ({
            label: this.countriesService.countryName(c), value: c
        }));
    }

    onCountryFilter(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.countrySearch$.next(input.value);
    }

    toggleSelection(): void {
        if (this.selectedIds.size === 0) {
            for (let i = 0; i < this.commodities().length; i++) {
                this.selectedIds.add(i);
            }
        } else {
            this.selectedIds.clear();
        }
    }

    isAllSelected(): boolean {
        return this.selectedIds.size === this.commodities().length && this.selectedIds.size > 0;
    }

    isSelectionIndeterminate(): boolean {
        return this.selectedIds.size > 0 && this.selectedIds.size < this.commodities().length;
    }

    toggleSelectRow(i: number): void {
        if (this.selectedIds.has(i)) {
            this.selectedIds.delete(i);
        } else {
            this.selectedIds.add(i);
        }
    }

    isSelected(i: number): boolean {
        return this.selectedIds.has(i);
    }

    doPatch(index: number, field: string, value: any): void {
        const indices = this.selectedIds.size === 0 ? [ index ] : [ ...Array.from(this.selectedIds), index ];
        switch (field) {
            case 'country':
                value = this.countriesService.resolveCountryCode(value, 'CA');
                break;
            case 'quantity':
                value = Math.ceil(Math.max(1, value));
                break;
            case 'weight':
                value = Math.max(0.01, value);
                break;
            case 'price':
                value = Math.max(0, value);
                break;
            case 'currency':
                value = matchChoices(value, this.currencyService.choices(), true);
                break;
            case 'pieces':
                value = Math.max(1, value);
                break;
            case 'unitOfMeasure':
                value = matchChoices(value, unitsOfMeasure, true);
                break;
            case 'length':
            case 'width':
            case 'height':
                value = Math.max(0, value);
                break;
        }
        const com: Partial<Commodity> = { [field]: value };
        let willChange = false;
        for (const i of indices) {
            const c = this.commodities()[i];
            if (c[field] !== value) {
                willChange = true;
            }
        }
        if (willChange) {
            setTimeout(() => this.store.dispatch(shipmentEditorPatchCommodities({ indices, com })));
        }
    }

    onFieldBlur(index: number, field: string, event: FocusEvent): void {
        const input = event.target as HTMLInputElement;
        this.doPatch(index, field, input.value);
    }

    onCheckbox(index: number, field: string, checked: boolean): void {
        this.doPatch(index, field, checked);
    }

    trackBy(index: number, item: any): number {
        if (item.kind === 'header') {
            return item.packageIndex;
        } else {
            return item.packageIndex + item.commodityIndex * 1000;
        }
    }

    addEmptyCommodity(packageIndex: number): void {
        this.store.dispatch(shipmentEditorAddEmptyCommodity({ packageIndex }));
    }

    addTemplateCommodity(packageIndex: number): void {
        this.store.dispatch(shipmentEditorAddCommodity({ packageIndex }));
    }

    deleteCommodities(): void {
        this.store.dispatch(shipmentEditorDeleteCommodities({ indices: [ ...this.selectedIds ] }));
        this.selectedIds.clear();
    }

    moveToPackage(packageIndex: number): void {
        this.store.dispatch(shipmentEditorMoveCommodities({ indices: [ ...this.selectedIds ], targetPackageIndex: packageIndex }));
    }
}
